/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        User Workspace Template Registry & Component Resolver (Task 4.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { DashboardLayout, type DashboardLayoutProps } from '../layouts/DashboardLayout';
import { ExecutiveLayout } from './executive/ExecutiveLayout';
import { MinimalZenLayout } from './zen/MinimalZenLayout';

export type WorkspaceTemplateSlug =
  | 'default'
  | 'executive'
  | 'minimal-zen'
  | 'zen'
  | 'minimal'
  | 'studio-immersive'
  | string;

export interface WorkspaceTemplateDefinition {
  id: string;
  name: string;
  description: string;
  component: React.ComponentType<DashboardLayoutProps>;
  isBuiltIn: boolean;
  version: string;
}

/**
 * Built-in workspace template mapping.
 */
export const WORKSPACE_TEMPLATE_REGISTRY: Record<string, WorkspaceTemplateDefinition> = {
  default: {
    id: 'default',
    name: 'Sophia Classic Studio (Default)',
    description: 'Dual-tier collapsible sidebar layout optimized for deep productivity and multi-studio multitasking.',
    component: DashboardLayout,
    isBuiltIn: true,
    version: '2.0.0',
  },
  executive: {
    id: 'executive',
    name: 'Executive Command Matrix',
    description: 'Modern horizontal top-navigation bar architecture with executive breadcrumbs ribbon and full-width workspace viewport.',
    component: ExecutiveLayout,
    isBuiltIn: true,
    version: '2.0.0',
  },
  'minimal-zen': {
    id: 'minimal-zen',
    name: 'Minimalist Zen Writer',
    description: 'Distraction-free focus writing environment with auto-hide floating navigation, word counter, and full-screen immersion.',
    component: MinimalZenLayout,
    isBuiltIn: true,
    version: '2.0.0',
  },
  zen: {
    id: 'zen',
    name: 'Minimalist Zen Writer (Alias)',
    description: 'Distraction-free focus writing environment with auto-hide floating navigation, word counter, and full-screen immersion.',
    component: MinimalZenLayout,
    isBuiltIn: true,
    version: '2.0.0',
  },
  minimal: {
    id: 'minimal',
    name: 'Minimalist Zen Writer (Alias)',
    description: 'Distraction-free focus writing environment with auto-hide floating navigation, word counter, and full-screen immersion.',
    component: MinimalZenLayout,
    isBuiltIn: true,
    version: '2.0.0',
  },
  'studio-immersive': {
    id: 'studio-immersive',
    name: 'Studio Immersive DAW',
    description: 'Ultra-wide horizontal command matrix layout optimized for multi-track audio DAW, video studio and side-by-side CAT translation.',
    component: ExecutiveLayout,
    isBuiltIn: true,
    version: '2.0.0',
  },
};

/**
 * Resolves a template layout component by ID with automatic fallback to default.
 *
 * @param templateId - The template slug requested (e.g. 'default', 'executive', 'minimal-zen', etc.)
 * @returns The React layout component to render
 */
export function getWorkspaceTemplate(
  templateId?: string | null
): React.ComponentType<DashboardLayoutProps> {
  if (!templateId) {
    return DashboardLayout;
  }

  const normalized = templateId.trim().toLowerCase();
  const definition = WORKSPACE_TEMPLATE_REGISTRY[normalized];

  if (definition && definition.component) {
    return definition.component;
  }

  // Graceful fallback to default layout
  return DashboardLayout;
}

/**
 * Checks if a given template ID is registered in the workspace registry.
 */
export function isValidWorkspaceTemplate(templateId?: string | null): boolean {
  if (!templateId) return false;
  return Boolean(WORKSPACE_TEMPLATE_REGISTRY[templateId.trim().toLowerCase()]);
}

/**
 * Returns a list of all unique workspace template definitions.
 */
export function listWorkspaceTemplates(): WorkspaceTemplateDefinition[] {
  const seen = new Set<string>();
  const list: WorkspaceTemplateDefinition[] = [];

  for (const item of Object.values(WORKSPACE_TEMPLATE_REGISTRY)) {
    if (!seen.has(item.id)) {
      seen.add(item.id);
      list.push(item);
    }
  }

  return list;
}
